import React, { useState } from 'react';
import {
  useGetProvenanceRecordQuery,
  useReviewProvenanceRecordMutation,
} from '../api/provenanceApi';
import type { ProvenanceReviewRequest } from '../types/provenance.types';
import CitationCard from './CitationCard';
import LicenseInfoCard from './LicenseInfoCard';
import MetadataSection from './MetadataSection';
import JsonCodeBlock from './JsonCodeBlock';
import ScoreBar from './ScoreBar';

interface ProvenanceRecordDetailProps {
  recordId: string;
  onClose: () => void;
  onReview: (recordId: string, action: 'approved' | 'rejected' | 'needs_revision') => void;
  onDelete?: (recordId: string) => void;
  currentStatus?: string;
}

const ProvenanceRecordDetail: React.FC<ProvenanceRecordDetailProps> = ({
  recordId,
  onClose,
  onReview,
  onDelete,
  currentStatus,
}) => {
  const { data: record, isLoading, error } = useGetProvenanceRecordQuery(recordId);
  const [reviewRecord] = useReviewProvenanceRecordMutation();

  const [reviewAction, setReviewAction] = useState<'approved' | 'rejected' | 'needs_revision' | null>(null);
  const [relevanceScore, setRelevanceScore] = useState<number>(3);
  const [feedbackNotes, setFeedbackNotes] = useState<string>('');

  const handleSubmitReview = async () => {
    if (!reviewAction) return;

    try {
      const review: ProvenanceReviewRequest = {
        action: reviewAction,
        citation_relevance_score: relevanceScore,
        feedback_notes: feedbackNotes || undefined,
      };

      await reviewRecord({
        id: recordId,
        review,
      }).unwrap();

      onReview(recordId, reviewAction);
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-50 text-green-700 ring-green-600/20';
      case 'rejected':
        return 'bg-red-50 text-red-700 ring-red-600/20';
      case 'in_review':
        return 'bg-yellow-50 text-yellow-800 ring-yellow-600/20';
      case 'queued':
        return 'bg-blue-50 text-blue-700 ring-blue-600/20';
      default:
        return 'bg-gray-50 text-gray-700 ring-gray-500/10';
    }
  };

  const getActionBadgeColor = (action: string) => {
    switch (action) {
      case 'approved':
        return 'bg-green-50 text-green-700 ring-green-600/20';
      case 'rejected':
        return 'bg-red-50 text-red-700 ring-red-600/20';
      case 'needs_revision':
        return 'bg-yellow-50 text-yellow-800 ring-yellow-600/20';
      default:
        return 'bg-gray-50 text-gray-700 ring-gray-500/10';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="bg-white rounded-lg border border-red-200 shadow-sm p-6">
        <div className="flex items-center gap-2 text-red-800">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-medium">Error loading record details</span>
        </div>
      </div>
    );
  }

  const queryText = (record as any).query_text || record.discovery_query || 'No query text available';

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Provenance Record Details</h2>
          {currentStatus && (
            <p className="text-sm text-gray-500 mt-1">
              Viewing from: <span className="font-medium capitalize">{currentStatus.replace('_', ' ')}</span> tab
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {onDelete && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this provenance record? This action cannot be undone.')) {
                  onDelete(recordId);
                }
              }}
              className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              title="Delete record"
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          )}
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Discovery Query Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Discovery Query</h3>
          <p className="text-sm text-gray-700 mb-4 whitespace-pre-wrap">{queryText}</p>
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700">Type:</span>
              <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800">
                {record.query_type}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700">Status:</span>
              <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${getStatusBadgeColor(record.review_status)}`}>
                {record.review_status}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{new Date(record.created_at).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Source Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Source Information</h3>
          <div className="space-y-3">
            {record.source_url && (
              <div>
                <span className="text-sm font-medium text-gray-700">Source URL</span>
                <div className="mt-1">
                  <a
                    href={record.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 break-all"
                  >
                    {record.source_url}
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            )}
            {record.content_preview && (
              <div>
                <span className="text-sm font-medium text-gray-700">Content Preview</span>
                <p className="mt-1 text-sm text-gray-600 line-clamp-3" dangerouslySetInnerHTML={{ __html: record.content_preview }} />
              </div>
            )}
            {record.scrape_timestamp && (
              <div>
                <span className="text-sm font-medium text-gray-700">Scrape Timestamp</span>
                <div className="mt-1 text-sm text-gray-600">
                  {new Date(record.scrape_timestamp).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* License Information */}
        {record.license_info && (
          <LicenseInfoCard licenseInfo={record.license_info} />
        )}

        {/* Source Metadata */}
        {record.source_metadata && Object.keys(record.source_metadata).length > 0 && (
          <MetadataSection
            title="Source Metadata"
            metadata={record.source_metadata}
            defaultExpanded={false}
          />
        )}

        {/* Pipeline Configuration */}
        {record.pipeline_config && Object.keys(record.pipeline_config).length > 0 && (
          <JsonCodeBlock
            data={record.pipeline_config}
            title="Pipeline Configuration"
            defaultExpanded={false}
          />
        )}

        {/* Citations */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Citations ({record.citations.length})
          </h3>
          {record.citations.length === 0 ? (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
              <p className="text-sm text-gray-500">No citations available</p>
            </div>
          ) : (
            <div className="space-y-4">
              {record.citations.map((citation) => (
                <CitationCard key={citation.id} citation={citation} />
              ))}
            </div>
          )}
        </div>

        {/* Reviews History */}
        {record.reviews.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Previous Reviews ({record.reviews.length})
            </h3>
            <div className="space-y-3">
              {record.reviews.map((review) => (
                <div key={review.id} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getActionBadgeColor(review.action)}`}>
                      {review.action}
                    </span>
                    {review.citation_relevance_score !== null && (
                      <ScoreBar
                        score={review.citation_relevance_score}
                        maxScore={5}
                        label="Relevance"
                        showPercentage={false}
                        className="flex-1 min-w-[200px]"
                      />
                    )}
                    <span className="text-xs text-gray-500">
                      {new Date(review.reviewed_at).toLocaleString()}
                    </span>
                  </div>
                  {review.feedback_notes && (
                    <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{review.feedback_notes}</p>
                  )}
                  {review.suggested_sources && review.suggested_sources.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-700">Suggested Sources: </span>
                      <span className="text-xs text-gray-600">{review.suggested_sources.join(', ')}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit Review Form */}
        {record.review_status === 'queued' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit Review</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Action
                </label>
                <select
                  value={reviewAction || ''}
                  onChange={(e) => setReviewAction(e.target.value as any)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="">Select action...</option>
                  <option value="approved">Approve</option>
                  <option value="rejected">Reject</option>
                  <option value="needs_revision">Needs Revision</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relevance Score (1-5)
                </label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={relevanceScore}
                  onChange={(e) => setRelevanceScore(parseInt(e.target.value))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <div className="mt-2">
                  <ScoreBar
                    score={relevanceScore}
                    maxScore={5}
                    showPercentage={false}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Feedback Notes
                </label>
                <textarea
                  value={feedbackNotes}
                  onChange={(e) => setFeedbackNotes(e.target.value)}
                  rows={4}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Add any feedback or notes about this review..."
                />
              </div>

              <button
                onClick={handleSubmitReview}
                disabled={!reviewAction}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
              >
                Submit Review
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProvenanceRecordDetail;
